import { useState, useEffect } from 'react'
import { fetchBlockFormats, createBlockFormat, updateBlockFormat, deleteBlockFormat } from '../supabase-client'
import { useDataCache } from '../contexts/DataCacheContext'
import { useSuppliers } from '../hooks/useSuppliers'
import { useAllStrains } from '../hooks/useStrains'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Separator } from '@/components/ui/separator'
import { OdinButton } from '@/components/ui/odin-button'
import { EditIcon } from '@/components/ui/icons/EditIcon'
import ToggleSwitch from './ToggleSwitch'
import SupplierDialog from './inventory/SupplierDialog'
import { Box, Check, ChevronsUpDown, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'

export default function BlockFormatsTab() {
  const { invalidateCache } = useDataCache()
  const { suppliers, refetch: refetchSuppliers } = useSuppliers({ activeOnly: true })
  const { strains } = useAllStrains({ activeOnly: true })
  const [formats, setFormats] = useState([])
  const [loading, setLoading] = useState(true)
  const [showDialog, setShowDialog] = useState(false)
  const [editingFormat, setEditingFormat] = useState(null)
  const [formData, setFormData] = useState({ name: '', weight_kg: '', size_dimensions: '', supplier_id: '', active: true, strain_ids: [] })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [strainPopoverOpen, setStrainPopoverOpen] = useState(false)
  const [showSupplierDialog, setShowSupplierDialog] = useState(false)

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const data = await fetchBlockFormats()
      setFormats(data)
    } catch (err) {
      console.error('Failed to load block formats:', err)
      setError('Failed to load block formats.')
    } finally {
      setLoading(false)
    }
  }

  const handleAddNew = () => {
    setEditingFormat(null)
    setFormData({ name: '', weight_kg: '', size_dimensions: '', supplier_id: '', active: true, strain_ids: [] })
    setShowDialog(true)
  }

  const handleEdit = (format) => {
    setEditingFormat(format)
    setFormData({
      name: format.name,
      weight_kg: String(format.weight_kg),
      size_dimensions: format.size_dimensions || '',
      supplier_id: format.supplier_id || '',
      active: format.active,
      strain_ids: (format.block_format_strains || []).map(bfs => bfs.strain_id)
    })
    setShowDialog(true)
  }

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.weight_kg) return

    try {
      setSaving(true)
      setError(null)
      const payload = {
        name: formData.name.trim(),
        weight_kg: parseFloat(formData.weight_kg),
        size_dimensions: formData.size_dimensions.trim() || null,
        supplier_id: formData.supplier_id || null,
        active: formData.active,
        strain_ids: formData.strain_ids
      }

      if (editingFormat) {
        await updateBlockFormat(editingFormat.id, payload)
      } else {
        await createBlockFormat(payload)
      }

      invalidateCache('blockFormats')
      await loadData()
      setShowDialog(false)
    } catch (err) {
      console.error('Failed to save block format:', err)
      setError('Failed to save. ' + (err.message || ''))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    if (!confirm('Delete this block format? This cannot be undone.')) return

    try {
      await deleteBlockFormat(id)
      invalidateCache('blockFormats')
      await loadData()
    } catch (err) {
      console.error('Failed to delete block format:', err)
      setError('Failed to delete. It may be in use by existing sales.')
    }
  }

  const toggleStrain = (strainId) => {
    setFormData(prev => {
      const ids = prev.strain_ids.includes(strainId)
        ? prev.strain_ids.filter(id => id !== strainId)
        : [...prev.strain_ids, strainId]
      return { ...prev, strain_ids: ids }
    })
  }

  const getStrainLabel = () => {
    if (formData.strain_ids.length === 0) return 'All strains'
    const names = formData.strain_ids
      .map(id => strains.find(s => s.id === id)?.name)
      .filter(Boolean)
    return names.join(', ')
  }

  const getStrainDisplay = (format) => {
    const bfs = format.block_format_strains || []
    if (bfs.length === 0) return 'All strains'
    return bfs.map(b => b.strain?.name).filter(Boolean).join(', ')
  }

  const handleSupplierSelect = (value) => {
    if (value === '__create_new__') {
      setShowSupplierDialog(true)
    } else {
      setFormData(prev => ({ ...prev, supplier_id: value === 'none' ? '' : value }))
    }
  }

  const handleSupplierCreated = async (newSupplier) => {
    setShowSupplierDialog(false)
    await refetchSuppliers()
    if (newSupplier?.id) {
      setFormData(prev => ({ ...prev, supplier_id: newSupplier.id }))
    }
  }

  if (loading) return <div className="p-4 text-sm text-gray-500">Loading block formats...</div>

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Box className="h-5 w-5 text-[#009689]" />
                Block Formats
              </CardTitle>
              <CardDescription>Configure available fruiting block sizes (weight in kg)</CardDescription>
            </div>
            <Button variant="create" onClick={handleAddNew}>
              + Add Format
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Weight (kg)</TableHead>
                  <TableHead>Size Dimensions</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Applies to</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {formats.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                      No block formats configured. Add one to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  formats.map(format => (
                    <TableRow key={format.id}>
                      <TableCell className="font-medium">{format.name}</TableCell>
                      <TableCell>{parseFloat(format.weight_kg).toFixed(1)} kg</TableCell>
                      <TableCell className="text-muted-foreground">{format.size_dimensions || '—'}</TableCell>
                      <TableCell className="text-muted-foreground">{format.supplier?.name || '—'}</TableCell>
                      <TableCell className="text-muted-foreground">{getStrainDisplay(format)}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          format.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {format.active ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <OdinButton variant="table-action" size="small" leftIcon={<EditIcon />} onClick={() => handleEdit(format)}>Edit</OdinButton>
                          <Button variant="destructive" size="sm" onClick={() => handleDelete(format.id)}>Delete</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingFormat ? 'Edit Block Format' : 'Add Block Format'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="formatName">Name</Label>
              <Input
                id="formatName"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Large Block"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="formatWeight">Weight (kg)</Label>
              <Input
                id="formatWeight"
                type="number"
                step="0.1"
                min="0.1"
                value={formData.weight_kg}
                onChange={(e) => setFormData(prev => ({ ...prev, weight_kg: e.target.value }))}
                placeholder="e.g. 4.5"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="formatDimensions">Size Dimensions</Label>
              <Input
                id="formatDimensions"
                value={formData.size_dimensions}
                onChange={(e) => setFormData(prev => ({ ...prev, size_dimensions: e.target.value }))}
                placeholder="e.g. 20x15x50cm"
              />
            </div>
            <div className="space-y-2">
              <Label>Supplier</Label>
              <Select
                value={formData.supplier_id}
                onValueChange={handleSupplierSelect}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a supplier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {suppliers.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                  <Separator className="my-1" />
                  <SelectItem value="__create_new__">
                    <span className="flex items-center gap-1.5 text-[#009689]">
                      <Plus className="h-3.5 w-3.5" />
                      Create new supplier
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Applies to</Label>
              <Popover open={strainPopoverOpen} onOpenChange={setStrainPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={strainPopoverOpen}
                    className="w-full justify-between bg-white hover:bg-gray-50"
                  >
                    <span className={cn('truncate', formData.strain_ids.length === 0 && 'text-muted-foreground')}>
                      {getStrainLabel()}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search strains..." />
                    <CommandList>
                      <CommandEmpty>No strains found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          onSelect={() => setFormData(prev => ({ ...prev, strain_ids: [] }))}
                          className="font-medium"
                        >
                          <Check className={cn('mr-2 h-4 w-4', formData.strain_ids.length === 0 ? 'opacity-100' : 'opacity-0')} />
                          All strains
                        </CommandItem>
                        <Separator className="my-1" />
                        {strains.map(strain => (
                          <CommandItem
                            key={strain.id}
                            value={strain.name}
                            onSelect={() => toggleStrain(strain.id)}
                          >
                            <Check className={cn('mr-2 h-4 w-4', formData.strain_ids.includes(strain.id) ? 'opacity-100' : 'opacity-0')} />
                            {strain.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex items-center gap-2">
              <ToggleSwitch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, active: checked }))}
              />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !formData.name.trim() || !formData.weight_kg}>
              {saving ? 'Saving...' : editingFormat ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SupplierDialog
        open={showSupplierDialog}
        onOpenChange={setShowSupplierDialog}
        supplier={null}
        onSuccess={handleSupplierCreated}
      />
    </>
  )
}
